import React, { useEffect, useRef, useState, useCallback } from 'react';

/**
 * LiveQualityCamera
 * Opens the device camera and shows a real-time quality indicator
 * (face detected / brightness / focus). Lets the user capture the
 * current frame as a File once the quality light turns green.
 *
 * Props:
 *  - facingMode: "user" (selfie) | "environment" (rear camera)
 *  - expectFace: bool — show face detection feedback
 *  - onCapture(file): called with a File when the user taps Capture
 *  - onCancel(): close without capturing
 *  - title: optional modal title
 */
export const LiveQualityCamera = ({
  facingMode = 'user',
  expectFace = true,
  onCapture,
  onCancel,
  title = 'Live Camera Capture',
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);
  const fileInputRef = useRef(null);

  const [error, setError] = useState('');
  const [denied, setDenied] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [ready, setReady] = useState(false);
  const [quality, setQuality] = useState({
    score: 0,
    level: 'red', // red | yellow | green
    label: 'Starting camera…',
    issues: [],
  });

  const stopStream = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  // Sample current video frame and compute simple quality metrics
  const sampleFrame = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) return;

    const maxDim = 240; // downscale for speed
    const scale = Math.min(1, maxDim / Math.max(video.videoWidth, video.videoHeight));
    const w = Math.max(1, Math.round(video.videoWidth * scale));
    const h = Math.max(1, Math.round(video.videoHeight * scale));
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(video, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);

    // Mean luma + Laplacian variance
    const gray = new Float32Array(w * h);
    let lumaSum = 0;
    for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
      const l = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
      gray[p] = l;
      lumaSum += l;
    }
    const meanLuma = lumaSum / (w * h);
    let sum = 0;
    let sumSq = 0;
    let n = 0;
    for (let y = 1; y < h - 1; y += 1) {
      for (let x = 1; x < w - 1; x += 1) {
        const idx = y * w + x;
        const v =
          -4 * gray[idx] +
          gray[idx - 1] +
          gray[idx + 1] +
          gray[idx - w] +
          gray[idx + w];
        sum += v;
        sumSq += v * v;
        n += 1;
      }
    }
    const variance = n ? sumSq / n - (sum / n) ** 2 : 0;

    // Face detection (best-effort, browser support varies)
    let faceCount = -1; // -1 = unsupported
    if (expectFace && typeof window !== 'undefined' && 'FaceDetector' in window) {
      try {
        const detector = new window.FaceDetector({ fastMode: true });
        const faces = await detector.detect(video);
        faceCount = faces?.length ?? 0;
      } catch {
        faceCount = -1;
      }
    }

    const issues = [];
    if (meanLuma < 40) issues.push('Too dark');
    if (meanLuma > 230) issues.push('Too bright / glare');
    if (variance < 60) issues.push('Blurry — hold steady');
    if (expectFace && faceCount === 0) issues.push('No face detected');

    let score = 100;
    if (variance < 60) score -= 35;
    if (meanLuma < 40 || meanLuma > 230) score -= 25;
    if (expectFace && faceCount === 0) score -= 30;
    score = Math.max(0, Math.min(100, score));

    let level = 'green';
    let label = 'Looking great — tap Capture';
    if (score < 60) {
      level = 'red';
      label = issues[0] || 'Adjust lighting and angle';
    } else if (score < 85) {
      level = 'yellow';
      label = issues[0] || 'Almost there — hold steady';
    }

    setQuality({ score, level, label, issues });
  }, [expectFace]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
          setReady(true);
          setDenied(false);
          setError('');
          // Start sampling
          intervalRef.current = setInterval(sampleFrame, 600);
        }
      } catch (e) {
        if (e?.name === 'NotAllowedError' || e?.name === 'SecurityError') {
          setDenied(true);
          setError('');
        } else if (e?.name === 'NotFoundError' || e?.name === 'OverconstrainedError') {
          setError('No camera was found on this device. Please use the file upload option instead.');
        } else {
          setError('Could not access the camera. Please use the file upload option instead.');
        }
      }
    })();

    return () => {
      active = false;
      stopStream();
    };
  }, [facingMode, sampleFrame, stopStream, retryKey]);

  const retryCamera = () => {
    stopStream();
    setDenied(false);
    setError('');
    setReady(false);
    setRetryKey((k) => k + 1);
  };

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) return;

    // Capture at native resolution
    const w = video.videoWidth;
    const h = video.videoHeight;
    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    const octx = out.getContext('2d');
    octx.drawImage(video, 0, 0, w, h);
    out.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `selfie-${Date.now()}.jpg`, { type: 'image/jpeg' });
        stopStream();
        onCapture(file);
      },
      'image/jpeg',
      0.92
    );
  };

  const handleCancel = () => {
    stopStream();
    onCancel?.();
  };

  // Fallback for devices where the in-app camera can't be accessed: let the user
  // pick/capture a selfie-with-ID photo, routed through the same onCapture pipeline.
  const handlePickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    stopStream();
    onCapture(file);
  };

  const levelColors = {
    red: 'bg-red-500',
    yellow: 'bg-yellow-400',
    green: 'bg-green-500',
  };
  const levelRing = {
    red: 'ring-red-200',
    yellow: 'ring-yellow-200',
    green: 'ring-green-200',
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="live-camera-modal">
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        onChange={handlePickFile}
        className="hidden"
        data-testid="camera-fallback-upload"
      />
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">{title}</h3>
          <button
            type="button"
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            data-testid="live-camera-close"
            aria-label="Close camera"
          >
            ×
          </button>
        </div>

        <div className="relative bg-black aspect-[4/3]">
          {denied ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center bg-gray-900 text-white overflow-y-auto" data-testid="camera-permission-guide">
              <svg className="w-10 h-10 text-orange-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
              <h4 className="font-bold text-base mb-1">Camera access is blocked</h4>
              <p className="text-sm text-white/80 mb-4">To take your verification selfie, please allow camera access:</p>
              <div className="text-left text-sm text-white/90 bg-white/5 rounded-xl p-4 max-w-sm w-full space-y-2 mb-4">
                <p><span className="font-semibold text-orange-300">In the GoodJob App:</span> when prompted, tap <span className="font-semibold">Allow</span> for Camera, then tap Try Again.</p>
                <p><span className="font-semibold text-orange-300">On Android:</span> Settings → Apps → <span className="font-semibold">GoodJob App</span> → Permissions → Camera → <span className="font-semibold">Allow</span>.</p>
                <p><span className="font-semibold text-orange-300">On iPhone:</span> Settings → scroll to <span className="font-semibold">GoodJob App</span> → <span className="font-semibold">Allow Camera</span>.</p>
                <p className="text-white/60">Then return here and tap <span className="font-semibold">Try Again</span>.</p>
                <p className="pt-1 border-t border-white/10"><span className="font-semibold text-orange-300">If unable to use your phone's camera,</span> upload your selfie photo with your ID using the button below.</p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-2">
                <button
                  type="button"
                  onClick={retryCamera}
                  className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold transition"
                  data-testid="camera-retry-btn"
                >
                  Try Again
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-5 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg font-semibold transition border border-white/20"
                  data-testid="camera-fallback-upload-btn"
                >
                  Upload selfie with ID
                </button>
              </div>
            </div>
          ) : error ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center text-white gap-3">
              <p data-testid="camera-error-text">{error}</p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold transition"
                data-testid="camera-fallback-upload-btn-error"
              >
                Upload selfie with ID
              </button>
            </div>
          ) : (
            <>
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                playsInline
                muted
                data-testid="live-camera-video"
              />
              {/* Live quality badge */}
              <div className="absolute top-3 right-3 flex items-center gap-2 bg-black/60 text-white px-3 py-1.5 rounded-full backdrop-blur-sm" data-testid="live-quality-badge">
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${levelColors[quality.level]} ring-4 ${levelRing[quality.level]}`}></span>
                <span className="text-xs font-medium">{quality.label}</span>
                <span className="text-xs text-white/70">· {quality.score}/100</span>
              </div>
              {/* Centering frame for selfie */}
              {expectFace && (
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                  <div className="border-2 border-white/40 rounded-[50%] w-2/3 h-3/4 max-w-[340px] max-h-[440px]"></div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-4 bg-gray-50 flex flex-col sm:flex-row gap-3 items-center justify-between">
          <div className="text-xs text-gray-500 flex-1">
            <span className="font-semibold">Tip:</span> Face the camera directly, hold your ID next to your face,
            and wait for the dot to turn <span className="inline-block w-2 h-2 rounded-full bg-green-500 align-middle mx-1"></span> green.
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium transition"
              data-testid="live-camera-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!ready || !!error || denied}
              onClick={handleCapture}
              className="px-5 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:shadow-lg font-semibold disabled:opacity-50 transition"
              data-testid="live-camera-capture"
            >
              📸 Capture
            </button>
          </div>
        </div>

        {/* Hidden working canvas */}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
};

export default LiveQualityCamera;
